// buildWelcomeMessage — pure construction of the initial bot welcome message.
//
// Extracted verbatim from `AIChatBot.js` (module-level `buildWelcomeMessage`) as
// part of the behavior-preserving refactor (issue #240). It derives the trip
// context and assembles the welcome message string + quick replies:
//   - when the trip has flights AND open gaps: a gap-aware greeting whose quick
//     replies are the derived gaps,
//   - otherwise: a generic greeting with the default hotel/dining/transport
//     quick replies.
//
// Clock injection: the source built the message with `id: Date.now()` and
// `timestamp: new Date()`. To keep this function pure (Req 3.1) the current time
// is injected as `now`; the container passes `new Date()` so observable behavior
// is unchanged. `id` is `now.getTime()` (numerically identical to `Date.now()`)
// and `timestamp` is the injected `now` Date.
//
// React-free and side-effect-free. The string/quick-reply assembly is preserved
// exactly so the committed characterization snapshots stay stable.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import deriveTripContext from './deriveTripContext';

/**
 * @typedef {import('./deriveTripContext').TripGap} TripGap
 */

/**
 * @typedef {Object} WelcomeMessage
 * @property {number} id
 * @property {'bot'} type
 * @property {string} content
 * @property {TripGap[]|Array<{label:string,value:string}>} quickReplies
 * @property {Date} timestamp
 */

/**
 * Builds the initial welcome message based on trip context.
 *
 * @param {Object|null|undefined} tripData - The trip record (may be missing).
 * @param {Date} now - Injected current time (the source used `Date.now()` /
 *   `new Date()` inline). The container supplies `new Date()`.
 * @returns {WelcomeMessage} The bot welcome message with quick replies.
 */
export function buildWelcomeMessage(tripData, now) {
  const ctx = deriveTripContext(tripData);
  let content;
  let quickReplies;

  if (ctx.hasFlights && ctx.gaps.length > 0) {
    const gapLabels = ctx.gaps.map(g => g.label).join(' • ');
    const destIntro = ctx.destination
      ? `I see you're heading to ${ctx.destination}${ctx.startDate ? ` on ${ctx.startDate}` : ''}!`
      : 'Your trip is looking good!';
    content = `Hi! I'm Dan, your AI travel assistant! ✈️\n\n${destIntro}\n\nYou have flights booked but still need:\n${gapLabels}\n\nWant me to help? Tap one to get started!`;
    quickReplies = ctx.gaps;
  } else {
    const destIntro = ctx.destination
      ? `Ready to help you plan your trip to ${ctx.destination}!`
      : 'Ready to help plan your trip!';
    content = `Hi! I'm Dan, your AI travel assistant! ✈️\n\n${destIntro}\n\nWhat would you like to explore?\n• 🏨 Hotel suggestions\n• 🍽️ Dining recommendations\n• 🚗 Transportation options\n• 🎯 Activities & attractions`;
    quickReplies = [
      { label: '🏨 Hotel', value: 'hotel' },
      { label: '🍽️ Dining', value: 'dining' },
      { label: '🚗 Transportation', value: 'transportation' },
    ];
  }

  return { id: now.getTime(), type: 'bot', content, quickReplies, timestamp: now };
}

export default buildWelcomeMessage;
